<template>
  <div :style="`--header-height: ${headerHeight}px;`">
    <GettyPage
      :id="isJpc ? 'jpc' : ''"
      :application-base-url="baseUrl"
      :header-options="{
        displayThreshold: headerDisplayThreshold
      }"
      :alerts="alerts"
    >
      <template v-if="isJpc" #header>
        <JPCAHeader
          :alerts="alerts"
          :base-nav="jpcaLinks.base"
          :primary-nav="jpcaLinks.primary"
          :force-on-dark="true"
          :display-threshold="headerDisplayThreshold"
          :modifier="jpcHeaderTheme"
        />
      </template>
      <Hero
        :key="componentKey"
        name="ResearchApplication"
        :data="heroData"
        :hide-links="true"
      >
        <template v-if="stickyHeaderItems.length" #content>
          <StickyHeader
            :collection-title="collectionTitle"
            :items="stickyHeaderItems"
            @sticky-header-height="handleStickyHeaderHeight"
          />
        </template>
      </Hero>
      <section>
        <!-- note: container is a getty-ui class that adds the page gutter -->
        <div :class="['gui-container', 'container', 'app-rcv']">
          <slot v-if="hasDefaultSlotContent"></slot>
          <router-view v-else />
        </div>
      </section>
      <template v-if="isJpc && showJpcFooter" #footer>
        <JPCAFooter :links="jpcFooterLinks" />
      </template>
    </GettyPage>
  </div>
</template>

<script>
import StickyHeader from "@/components/atoms/stickyHeader/index.vue";
import {
  Hero,
  GettyPage,
  useLocalization,
  useEventBus,
  globalHeaderMixin,
  slotContentMixin
} from "@thegetty-private/getty-ui";
import { AnalyticsMixin as analyticsMixin } from "@thegetty-private/rad-app-utils";
import app_nav from "@data/app_nav.json";
import jpca_app_nav from "@data/jpca_app_nav.json";
import JPCAHeader from "@/components/globals/jpcaHeader/index.vue";
import JPCAFooter from "@/components/globals/jpcaFooter/index.vue";

import * as configHelper from "@/helpers/configHelper";
import * as contentHelper from "@/helpers/ContentHelper";
import { computed } from "vue";

export default {
  name: "LayoutBase",
  components: {
    GettyPage,
    Hero,
    StickyHeader,
    JPCAHeader,
    JPCAFooter
  },
  mixins: [analyticsMixin, globalHeaderMixin, slotContentMixin],
  provide() {
    return {
      /**
       * The IIIF version used for processing manifests
       */
      defaultIiifVersion: computed(() => configHelper.getIIIFVersion()),
      /**
       * updates the current page title (used by child pages)
       * @param {string} title
       */
      updatePageTitle: (title) => {
        this.updateTitle(title);
      },
      /**
       * provides the StickyHeaderHeight for the CollectionTree observer
       */
      stickyHeaderHeight: computed(() => this.stickyHeaderHeight),
      /**
       * provides the StickyHeaderItem clicked for routing in the
       * CollectionInventory
       */
      stickyHeaderItem: computed(() => this.currentStickyHeaderItem),
      /**
       * IIIF BaseUrl
       */
      defaultIiifBaseUrl: computed(() => configHelper.getIIIFBaseUrl())
    };
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * Alerts to display on the page
       */
      alerts: [],
      /**
       * The collection title
       */
      collectionTitle: undefined,
      /**
       * The current URL path
       */
      currentPath: undefined,
      /**
       * The current page title
       */
      currentTitle: undefined,
      /**
       * Getty-UI event bus for reporting when a breadcrumbs item is
       * clicked
       */
      eventBus: useEventBus(),
      /**
       * Items for the ContainerList breadcrumbs
       */
      stickyHeaderItems: [],
      /**
       * The breadcrumb item clicked in the StickyHeader
       */
      currentStickyHeaderItem: undefined,
      /**
       * Sticky header height provided for the CollectionTree
       */
      stickyHeaderHeight: undefined,
      /**
       * GuiHeader display threshold, y-offset at which to show header
       * on scroll up
       */
      headerDisplayThreshold: undefined,
      /**
       * Scroll height at which to display sticky header
       */
      showStickyHeaderAt: undefined,
      /**
       * whether we're in JPC mode or not
       */
      isJpc: configHelper.isJPC(),
      /**
       * used to increment and rerender the hero to toggle between between the stickyHeader
       * and regular hero
       */
      componentKey: 0,
      /**
       * the links for the footer
       */
      jpcFooterLinks: [
        {
          to: { name: "privacy" },
          text: this.translatePart("g-jpca-footer.links.privacy")
        },
        {
          to: { name: "terms" },
          text: this.translatePart("g-jpca-footer.links.terms")
        },
        {
          to: { name: "accessibility" },
          text: this.translatePart("g-jpca-footer.links.accessibility")
        },
        {
          to: { name: "contact" },
          text: this.translatePart("g-jpca-footer.links.contact")
        }
      ],
      /**
       * Page metadata to pass through to Analytics
       */
      pageMetadata: undefined,
      /**
       * Show JPC footer after the DOM updates
       */
      showJpcFooter: false,
      /**
       * JPC nav header theme
       */
      jpcHeaderTheme: undefined
    };
  },
  computed: {
    baseUrl() {
      return configHelper.getGettyUIBaseUrl();
    },
    /**
     * Application Title
     */
    appTitle() {
      return contentHelper.getApplicationHeaderTitle(this.translatePart);
    },
    /**
     * Object of data props to be passed into the Hero component regarding what to display
     */
    heroData() {
      let heroData = {
        title: contentHelper.getApplicationTitle(this.translatePart),
        showLinksHeading: false,
        showStickyHeaderAt: this.showStickyHeaderAt
      };

      // take navigation links data and translate applicable fields
      let navLinks = app_nav.map((link) => {
        link.text = this.translatePart(link.text);
        link.ariaLabel = this.translatePart(link.ariaLabel);
        link.active = false;
        if (link && link.to && link.to.name == this.$route.name) {
          link.active = true;
        }

        // overwrite the home route to /home for JPC
        // if (this.isJpc && link.to.name == "home") {
        //   link.to.name = "homepage";
        // }

        return link;
      });

      // check to see if feedback link should be included in navigation
      if (configHelper.includeFeedbackLink()) {
        navLinks.push({
          text: this.translatePart("application_nav.feedback"),
          url: "https://forms.gle/CMoF3w64QMmuxn1x5",
          ariaLabel: this.translatePart("application_nav.feedback_aria")
        });
      }

      // the latter two are for JPCA
      if (["/", "/research", "/research/"].includes(this.$route.path)) {
        heroData.headingLevel = 1;
      } else {
        heroData.headingLevel = 2;
      }

      if (this.isJpc) {
        heroData.backgroundColor = "black";
        heroData.title = "";
        heroData.links = [];
      } else {
        heroData.links = navLinks;
      }

      // add hero heading level based on path
      return heroData;
    },
    /**
     * The JPCA nav links
     */
    jpcaLinks() {
      // take navigation links data and translate applicable fields
      let navLinks = {};

      for (let key of Object.keys(jpca_app_nav)) {
        navLinks[key] = navLinks[key] ?? [];
        // build ordered navigation links
        for (let link of jpca_app_nav[key]) {
          if (
            !configHelper.includeFeedbackLinkInJPC() &&
            link.to.name == "feedback"
          ) {
            continue;
          }
          let linkName = link.to.name;
          link.active = linkName == this.$route.name;
          navLinks[key].push({
            ...link,
            text: this.translatePart(link.text)
          });
        }
      }

      return navLinks;
    }
  },
  watch: {
    $route(to, from) {
      // if the route doesn't change, don't do anything
      if (to === from) {
        return;
      }
      this.currentTitle = undefined;
      // if routing to collection container-list tab set GuiHeader display threshold
      this.headerDisplayThreshold = this.getHeaderDisplayThreshold();
      this.updatePath();

      // clear out any breadcrumbs if we're not on the ContainerList
      if (!this.isContainerList(this.$route) && this.stickyHeaderItems.length) {
        this.stickyHeaderItems = [];
      }

      // reset metadata fields
      this.collectionTitle = undefined;
      this.pageMetadata = undefined;
      this.setJpcHeaderTheme();
    },
    /**
     * if the title changes and the title is not undefined, send a pageview
     *
     * @param {string} to - the new title
     * @param {string} from - the old title
     */
    currentTitle(to, from) {
      if (from != undefined || to == undefined) {
        return;
      }

      // check for additional metadata to pass to Plausible
      // but allow for time for all data to come back up
      setTimeout(() => {
        let extra = {};
        if (this.collectionTitle) {
          extra.collection = this.collectionTitle;
        }

        if (this.pageMetadata) {
          extra = { ...extra, ...this.pageMetadata };
        }

        this.sendPageview(this.currentTitle, extra);
      }, 500);
    },
    /**
     * Check if we should rerender the hero based on the number of sticky header items
     *
     * @param {string} to - the new title
     * @param {string} from - the old title
     */
    stickyHeaderItems(to, from) {
      // if the length has changed and we're toggling between having any stickyHeaderItems
      // update the hero using the componentKey
      if (to.length != from.length) {
        if (to.length == 0 || from.length == 0) {
          this.componentKey = this.componentKey + 1;
        }
      }
    }
  },
  mounted() {
    this.updatePath();
    this.setJpcHeaderTheme();

    // listener for ImageViewerInteractionEvent analytics data
    this.eventBus.on("ImageViewerInteractionEvent", this.sendEvent);
    this.eventBus.on("AnalyticsEvent", this.sendEvent);
    // Set the sticky header items
    this.eventBus.on("linkTree", (items) => {
      this.stickyHeaderItems = items;
    });

    // Set the currentStickyHeaderItem to the breadcrumb clicked so
    // that we can scroll to it if the breadcrumb hash is already in
    // the url
    this.eventBus.on("breadcrumbItemClick", (item) => {
      this.currentStickyHeaderItem = item;
    });

    // Reset the current currentStickyHeaderItem after we've scrolled to it
    this.eventBus.on("scrolledToBreadcrumbItem", () => {
      this.currentStickyHeaderItem = undefined;
    });

    this.eventBus.on("collectionTitle", (title) => {
      this.collectionTitle = title;
      // once collection list is loaded set sticky header
      this.showStickyHeaderAt = this.getShowStickyHeaderAt();
    });

    this.eventBus.on("PageMetadata", (metadata) => {
      this.pageMetadata = metadata;
    });

    // if on collection container-list tab set GuiHeader display threshold
    this.headerDisplayThreshold = this.getHeaderDisplayThreshold();

    const alert = configHelper.getAlert();
    if (alert) {
      this.alerts[0] = alert;
    }

    // wait to load the footer
    this.$nextTick(() => {
      this.showJpcFooter = true;
    });
  },
  unmounted() {
    this.eventBus.off("ImageViewerInteractionEvent", this.sendEvent);
    this.eventBus.off("AnalyticsEvent", this.sendEvent);
  },
  methods: {
    /**
     * Gets the Hero offset height if on the collection container-list tab
     *
     * @returns {number|undefined}
     */
    getHeaderDisplayThreshold() {
      if (this.isContainerList(this.$route)) {
        const el = document.querySelector(".o-hero__container");
        // return 0 for testing
        return el ? el.offsetHeight : 0;
      }
      return undefined;
    },
    /**
     * Gets the offset height of first collection link
     */
    getShowStickyHeaderAt() {
      const el = document.querySelector(".o-container-list__column--tree");
      return el ? window.scrollY + el.getBoundingClientRect().top : undefined;
    },
    /**
     * Checks a route object to see if we're routing to the ContainerList
     *
     * @param {object} - A route object
     * @returns {boolean} - whether the route object is for the ContainerList tab
     */
    isContainerList(route) {
      // is it a collection route?
      if (route.name == "collection") {
        // is it the container-list tab?
        if (route.query.tab == "container-list") {
          return true;
        }
      }
      return false;
    },
    /**
     * Update the current url
     * @param {string} path - the current url
     */
    updatePath(path = this.$route.fullPath) {
      this.currentPath = path;
    },
    /**
     * Update the page title
     * @param {string} title
     */
    updateTitle(title) {
      if (this.currentTitle == undefined) {
        this.currentTitle = title;
      }
    },
    /**
     * Sticky header event handler, sets current sticky header
     */
    handleStickyHeaderHeight(e) {
      this.stickyHeaderHeight = e;
    },
    /**
     * Gets JPC header theme variation
     * @returns {string}
     */
    setJpcHeaderTheme() {
      if (this.isJpc) {
        if (
          this.$route.name !== "home" &&
          this.$route.params.slug == undefined
        ) {
          this.jpcHeaderTheme = "light";
          return;
        }
      }
      this.jpcHeaderTheme = undefined;
    }
  }
};
</script>

<style lang="scss">
@import "./layout-base";
</style>
